import cx from 'classnames';
import { ComponentProps, PropsWithChildren, ReactNode, useRef } from 'react';
import Box, { BoxStyleProps } from '../Box';
import useClickOutside from '../useClickOutside';
import styles from './Popover.module.css';

export type PopoverTriggerProps = Pick<
  ComponentProps<'div'>,
  'aria-haspopup' | 'aria-expanded'
>;

export type PopoverPlacement =
  | 'bottom-start'
  | 'bottom'
  | 'bottom-end'
  | 'top-start'
  | 'top'
  | 'top-end'
  | 'left-start'
  | 'left'
  | 'left-end'
  | 'right-start'
  | 'right'
  | 'right-end';

export interface PopoverProps extends BoxStyleProps {
  className?: string;
  open?: boolean;
  renderTrigger?: (props: PopoverTriggerProps) => ReactNode;
  onRequestClose?: () => void;
  placement?: PopoverPlacement;
}

// TODO: Create a popper component to use for floating elements such as Tooltip, Popper etc.
export default function Popover({
  className,
  open,
  renderTrigger,
  onRequestClose,
  placement = 'bottom',
  children,
  ...otherProps
}: PropsWithChildren<PopoverProps>): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);

  useClickOutside({
    ref,
    onOutsideClick: onRequestClose ? onRequestClose : () => undefined,
  });

  return (
    <Box {...otherProps}>
      <div className={cx(styles['base'], className)} ref={ref}>
        {renderTrigger &&
          renderTrigger({
            'aria-haspopup': 'true',
            'aria-expanded': open ? 'true' : undefined,
          })}
        {open && (
          <div
            className={cx(
              [styles[`popover--placement-${placement}`]],
              styles['popover']
            )}
          >
            {children}
          </div>
        )}
      </div>
    </Box>
  );
}
